import { createMemory } from "@/components/memories/api.ts";
import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { queryClient } from "@/lib/query-client.ts";
import { useMutation } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { SmartTextarea } from "../messages/Omnibox/SmartTextarea.tsx";
import { errorToast } from "../ui/toast.tsx";

interface AddMemoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AddMemoryDialog = ({ open, onOpenChange }: AddMemoryDialogProps) => {
  const [memoryContent, setMemoryContent] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const createMemoryMutation = useMutation({
    mutationFn: createMemory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["memories"] });
      setMemoryContent("");
      onOpenChange(false);
    },
    onError: (error) => {
      errorToast("Failed to create memory");
      console.error("Error creating memory:", error);
    },
  });

  const handleSubmit = () => {
    if (!memoryContent.trim()) return;
    createMemoryMutation.mutate({ content: memoryContent });
  };

  const handleCancel = () => {
    setMemoryContent("");
    onOpenChange(false);
  };

  const isSubmitDisabled = !memoryContent.trim() || createMemoryMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add a new memory</DialogTitle>
          <DialogDescription>
            What memory or detail should Okthx consider when providing assistance for you?
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <label htmlFor="memory-input" className="text-sm font-medium">
              Memory
            </label>
            <div className="rounded-md border border-input bg-background px-3 py-2 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
              <SmartTextarea
                ref={textareaRef}
                value={memoryContent}
                onChange={setMemoryContent}
                placeholder="Add a detail for Okthx to remember. Use @ to mention people."
                className="min-h-[60px] text-sm"
                initialRows={3}
                autoFocus
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={handleCancel}
            data-statsig-name="memories-add-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            data-statsig-name="memories-add-submit"
          >
            Add memory
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
